/**
 * Hash router. `#/overview`, `#/live`, `#/sessions`, `#/sessions/:id`,
 * `#/sessions/:id/replay`, `#/admin`, `#/admin/:tab`. Anything else is the
 * overview. Hash routing means the Vercel rewrite is irrelevant and a link
 * can be pasted anywhere.
 */
import { useEffect, useState } from 'react';

export type AdminTab = 'users' | 'equipment' | 'prompts';

export type Route =
  | { name: 'overview' }
  | { name: 'live' }
  | { name: 'sessions' }
  | { name: 'session'; id: string }
  | { name: 'replay'; id: string }
  | { name: 'admin'; tab: AdminTab };

function parse(hash: string): Route {
  const h = hash.replace(/^#\/?/, '').replace(/\/+$/, '');
  if (h.startsWith('sessions/')) {
    const rest = h.slice('sessions/'.length);
    if (rest.endsWith('/replay')) {
      return { name: 'replay', id: rest.slice(0, -'/replay'.length) };
    }
    return { name: 'session', id: rest };
  }
  if (h === 'sessions' || h === 'history') return { name: 'sessions' };
  if (h === 'live') return { name: 'live' };
  if (h === 'admin') return { name: 'admin', tab: 'users' };
  if (h.startsWith('admin/')) {
    const tab = h.slice('admin/'.length);
    if (tab === 'users' || tab === 'equipment' || tab === 'prompts') return { name: 'admin', tab };
    return { name: 'admin', tab: 'users' };
  }
  return { name: 'overview' };
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parse(window.location.hash));
  useEffect(() => {
    const handler = () => setRoute(parse(window.location.hash));
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, []);
  return route;
}

export function href(route: Route): string {
  switch (route.name) {
    case 'session':
      return `#/sessions/${route.id}`;
    case 'replay':
      return `#/sessions/${route.id}/replay`;
    case 'admin':
      return `#/admin/${route.tab}`;
    default:
      return `#/${route.name}`;
  }
}

export function go(route: Route): void {
  window.location.hash = href(route);
}
